import { RepriceServiceMutationVariables } from "@graphql/api";
import Session from "@lib/session";

// Creates a graphql services query from our res.

function dropTypename(fieldValues: be.FieldValue[]) {
  return fieldValues?.map((fieldValue) => {
    return { field: fieldValue.field, value: fieldValue.value };
  });
}

function locationVars(loc: be.Location) {
  if (!loc) return undefined;
  return {
    code: loc.code,
    address: loc.address,
    city: loc.city,
    state: loc.state,
    zip: loc.zip,
    name: loc.name,
    sector: loc.sector,
    county: loc.county,
    country: loc.country,
    latLng: {
      lat: loc.latLng?.lat,
      lng: loc.latLng?.lng,
    },
    googlePlaceId: loc.googlePlaceId,
    type: loc.type,
  };
}

function returnTripStatus(roundtrip: boolean, legNum: number) {
  if (!roundtrip) return "N";
  if (roundtrip && legNum == 0) return "F";
  if (roundtrip && legNum == 1) return "S";
}

export default function variables(
  res: be.Reservation,
  roundtrip: boolean,
  legNum: number,
  mode: "clean" | "full" | "withBaggageAndPassengers" = "full",
  slowRecalculateEntireFare: boolean,
): RepriceServiceMutationVariables {
  // Accumulate fare components.
  let fareComponents: be.FieldValue[] = [];
  if (res.service && Object.entries(res.service).length > 0) {
    if (res.service.fareComponents) fareComponents = dropTypename(res.service.fareComponents);

    if (res.service.selectedTime?.fareComponents) {
      fareComponents.push(...dropTypename(res.service.selectedTime.fareComponents));
    } else if (res.service.times) {
      const defaultTime = res.service.times?.find((time) => time.suggested) || res.service.times[0];
      if (defaultTime?.fareComponents)
        fareComponents.push(...dropTypename(defaultTime.fareComponents));
    }
  }

  // If user gratuity override is set then clear out groupGratuity and serviceGratuity.
  if (res.userGratuityOverride && res.service?.fareComponents) {
    fareComponents = fareComponents.filter(
      (comp) => comp.field !== "groupGratuity" && comp.field !== "serviceGratuity",
    );
  }

  // If this is the second leg of a roundtrip we need to send the first leg's pickup time.
  // Some sites may use that to determine a discount.
  let compPickupTime;
  if (roundtrip && legNum == 1)
    compPickupTime = Session.getLeg(0).service?.selectedTime?.pickupTime;

  let userGratuity = 0;
  const existingUserGrat = res.service?.fareComponents?.find(
    (fc) => fc.field === "userGratuity" || fc.field === "user_gratuity",
  )?.value;

  if (res.userGratuityPercentage !== null && res.userGratuityPercentage !== undefined) {
    userGratuity = res.userGratuityPercentage * 0.01 * (res.service.gratuityFare || 0);
    userGratuity = Math.round(userGratuity * 100) / 100; // Round
  } else if (existingUserGrat) {
    userGratuity = parseFloat(existingUserGrat);
  }

  const totalPassengerCount =
    res.passengers?.reduce((acc, psgr) => acc + Number(psgr.value), 0) || 0;
  let vars;
  const baseQuery = {
    sourceType: Number.isInteger(res.sourceType) ? res.sourceType : 60,
  };
  const baseRes = {
    compPickupTime,
    dropoff: res.dropoff && locationVars(res.dropoff),
    epicPassValidated: res.epicPassValidated,
    flight: {
      flightTime: res.flight?.flightTime,
      flightType: res.customerRequest ? "R" : res.flight?.flightType,
      flightNumber: res.flight?.flightNumber,
      airline: res.flight?.airline,
    },
    internalId: res.resIdInternal,
    open: res.open,
    pickup: res.pickup && locationVars(res.pickup),
    requestedPickupTime: res.requestedPickupTime,
    returnTripStatus: res.returnTripStatus
      ? res.returnTripStatus
      : returnTripStatus(roundtrip, legNum),
    serviceAreaId: Session.getBooking().serviceArea,
  };
  if (mode == "clean") {
    vars = { ...baseQuery, res: baseRes };
  } else if (mode == "withBaggageAndPassengers") {
    vars = {
      ...baseQuery,
      res: {
        ...baseRes,
        bagCount: res.bagCount,
        passengers: totalPassengerCount > 0 ? res.passengers?.map(p => ({ field: p.field, value: String(p.value) })) : undefined,
      },
    };
  } else {
    let payments = !res.payments
      ? undefined
      : {
          items: res.payments?.items?.map((payment) => ({
            points: payment.points,
            programId: payment.programId,
            type: payment.type,
          })),
          totalPaymentAmount: res.payments?.totalPaymentAmount,
          amountDue: res.payments?.amountDue,
        };

    vars = {
      ...baseQuery,
      recalcEntireFare: slowRecalculateEntireFare,
      res: {
        ...baseRes,
        perPassengerRates: !slowRecalculateEntireFare ? res.service?.perPassengerRates : undefined,
        bagCount: res.bagCount,
        fareComponents: fareComponents,
        mileage: res.service?.mileage,
        passengers: res.passengers?.map(p => ({ field: p.field, value: String(p.value) })),
        payments,
        pickupExtra: res.service?.selectedPickupExtra?.value,
        reservationFlags: res.flags,
        serviceExtras: res.serviceExtras?.map(se => ({ field: se.field, value: String(se.value) })),
        serviceType: res.service?.serviceType,
        stops: res.stops
          ?.filter((stop) => stop.location?.code)
          .map((stop) => ({
            location: locationVars(stop.location),
            name: stop.name,
            comments: stop.comments,
            directions: stop.directions,
          })),
        travelTime: res.service?.travelTime,
        userGratuity: userGratuity,
        userGratuityOverride: res.userGratuityOverride,
        userGratuityPercentage: res.userGratuityPercentage,
      },
    };
  }

  return vars;
}
